import { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import i18n from "@/i18n/config";

const SUPPORTED_LANGS = ["en", "de"] as const;
type Lang = (typeof SUPPORTED_LANGS)[number];

export function useLanguage() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang: Lang = SUPPORTED_LANGS.includes(lang as Lang) ? (lang as Lang) : "en";

  // Sync i18n in an effect (never during render) to avoid setState-in-render loops.
  useEffect(() => {
    const baseLang = i18n.language?.split("-")[0];
    if (baseLang !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang]);

  const switchLanguage = (newLang: Lang) => {
    if (newLang === currentLang) return;
    const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, "") || "/";
    navigate(`/${newLang}${pathWithoutLang === "/" ? "" : pathWithoutLang}${location.hash}`);
  };

  const localizedPath = (path: string) => {
    if (path.startsWith("/#")) return `/${currentLang}${path.slice(1)}`;
    return `/${currentLang}${path}`;
  };

  return { currentLang, switchLanguage, localizedPath };
}
